import { useState } from "react";
import { ArrowLeft, ChevronRight, ZoomIn, ZoomOut, X, Circle, Crown, Loader2, Volume2, VolumeX, Play, Pause, Download, Monitor, FileVideo } from "lucide-react";

export function PresentationOverlay({
  diagramTitle,
  presSlides,
  presSlideIdx,
  prevSlide,
  nextSlide,
  goToSlide,
  presentationZoomIn,
  presentationZoomOut,
  exitPresentationWithCleanup,
  ttsVoice,
  setTtsVoice,
  ttsEnabled,
  setTtsEnabled,
  ttsLoading,
  isRecordingVideo,
  startVideoRecording,
  stopVideoRecording,
  requireEnterprise,
  userPlan,
  user,
  autoPlaying,
  onToggleAutoPlay,
  videoReady,
  onDownloadVideo,
  onConvertMp4,
  mp4Converting,
  mp4Ready,
  mp4Url,
  recordingDuration,
}) {
  const [showRecPanel, setShowRecPanel] = useState(false);
  const isEnt = userPlan === "enterprise" || user?.role === "admin";

  return (
    <>
      {/* Click zones for navigation */}
      {!autoPlaying && (
        <div className="absolute inset-0 z-40 flex pointer-events-none">
          <div className="w-1/3 h-full pointer-events-auto cursor-pointer" onClick={prevSlide} />
          <div className="w-1/3 h-full" />
          <div className="w-1/3 h-full pointer-events-auto cursor-pointer" onClick={nextSlide} />
        </div>
      )}

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-8 py-4 bg-gradient-to-b from-white/90 via-white/50 to-transparent z-50 pointer-events-none backdrop-blur-sm">
        <div className="pointer-events-auto">
          <h1 className="font-heading text-xl font-bold text-gray-900">{diagramTitle}</h1>
          <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">
            {presSlides[presSlideIdx]?.label || "Présentation"}
          </span>
        </div>
        <div className="pointer-events-auto flex items-center gap-2">
          {presSlides.length > 0 && (
            <span className="text-xs font-semibold text-gray-500 bg-white/80 backdrop-blur-sm border border-gray-200 px-3 py-1.5 rounded-full">
              {presSlideIdx + 1} / {presSlides.length}
            </span>
          )}
          {/* Recording indicator — top right, always visible when recording */}
          {isRecordingVideo && (
            <div className="flex items-center gap-2 bg-rose-600 text-white px-3 py-1.5 rounded-full shadow-lg animate-pulse" data-testid="rec-indicator">
              <Circle size={8} className="fill-white" />
              <span className="text-[10px] font-bold uppercase tracking-wider">REC</span>
              {recordingDuration > 0 && (
                <span className="text-[10px] font-mono">{Math.floor(recordingDuration / 60)}:{String(recordingDuration % 60).padStart(2, "0")}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Auto-play overlay text */}
      {autoPlaying && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
          <div className="bg-indigo-600/90 text-white px-4 py-2 rounded-full text-[10px] font-bold flex items-center gap-2 backdrop-blur-sm shadow-lg animate-in fade-in">
            <Play size={10} className="fill-white" /> Lecture automatique en cours
          </div>
        </div>
      )}

      {/* Bottom navigation bar */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2" data-testid="présentation-controls">
        {/* Auto-play button */}
        <button
          onClick={() => {
            if (!ttsEnabled && !autoPlaying) {
              if (!requireEnterprise("Voix off TTS")) return;
              setTtsEnabled(true);
            }
            onToggleAutoPlay?.();
          }}
          className={`w-10 h-10 rounded-xl shadow-lg flex items-center justify-center transition-colors ${
            autoPlaying
              ? "bg-indigo-600 text-white"
              : "bg-white/90 backdrop-blur-sm border border-gray-200 text-indigo-600 hover:bg-indigo-50"
          }`}
          data-testid="pres-auto-play"
          title={autoPlaying ? "Mettre en pause" : "Lecture automatique avec voix off"}
        >
          {autoPlaying ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
        </button>

        <div className="w-px h-6 bg-gray-200" />

        <button
          onClick={prevSlide}
          disabled={presSlideIdx === 0 || autoPlaying}
          className="w-10 h-10 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-xl shadow-lg hover:bg-white transition-colors flex items-center justify-center text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="pres-prev"
        >
          <ArrowLeft size={16} />
        </button>

        {/* Slide dots */}
        <div className="flex items-center gap-1 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-xl px-3 py-2 shadow-lg">
          {presSlides.map((slide, idx) => (
            <button
              key={idx}
              onClick={() => !autoPlaying && goToSlide(idx)}
              className={`transition-all rounded-full ${
                idx === presSlideIdx
                  ? "w-6 h-2.5 bg-indigo-600"
                  : idx < presSlideIdx
                    ? "w-2.5 h-2.5 bg-indigo-300"
                    : "w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400"
              }`}
              title={slide.label}
              disabled={autoPlaying}
            />
          ))}
        </div>

        <button
          onClick={nextSlide}
          disabled={presSlideIdx >= presSlides.length - 1 || autoPlaying}
          className="w-10 h-10 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-xl shadow-lg hover:bg-white transition-colors flex items-center justify-center text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid="pres-next"
        >
          <ChevronRight size={16} />
        </button>

        <div className="w-px h-6 bg-gray-200 mx-1" />

        <button
          onClick={presentationZoomOut}
          className="w-9 h-9 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg shadow-lg hover:bg-white transition-colors flex items-center justify-center text-gray-600"
          data-testid="pres-zoom-out"
        >
          <ZoomOut size={14} />
        </button>
        <button
          onClick={presentationZoomIn}
          className="w-9 h-9 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg shadow-lg hover:bg-white transition-colors flex items-center justify-center text-gray-600"
          data-testid="pres-zoom-in"
        >
          <ZoomIn size={14} />
        </button>
        <button
          onClick={exitPresentationWithCleanup}
          className="w-9 h-9 bg-rose-500 text-white rounded-lg shadow-lg hover:bg-rose-600 transition-colors flex items-center justify-center"
          data-testid="pres-exit-btn"
        >
          <X size={14} />
        </button>
      </div>

      {/* Right-side panel: REC + TTS controls */}
      <div className="absolute bottom-6 right-6 z-50 flex flex-col items-end gap-2">
        {/* REC Panel (expanded) — only for starting or downloading */}
        {showRecPanel && !isRecordingVideo && (
          <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl shadow-2xl p-3 w-56 animate-in fade-in slide-in-from-bottom-2" data-testid="rec-panel">
            <p className="text-[10px] font-bold text-gray-900 mb-2 flex items-center gap-1.5">
              <Monitor size={11} /> Enregistrement ecran
            </p>
            {!videoReady && (
              <>
                <p className="text-[8px] text-gray-400 mb-2 leading-relaxed">
                  Votre ecran sera capture pendant la présentation. A la fin, vous pourrez telecharger la video.
                </p>
                <button
                  onClick={() => {
                    if (!requireEnterprise("Enregistrement video")) return;
                    startVideoRecording();
                    setShowRecPanel(false);
                  }}
                  className="w-full py-2 rounded-lg text-[10px] font-bold bg-rose-600 text-white hover:bg-rose-700 transition-colors flex items-center justify-center gap-1.5"
                  data-testid="start-rec-btn"
                >
                  <Circle size={8} className="fill-white" /> Démarrer l'enregistrement
                  {!isEnt && <Crown size={8} className="text-amber-300 ml-1" />}
                </button>
              </>
            )}
            {videoReady && (
              <>
                <p className="text-[9px] text-emerald-600 font-semibold mb-2">Enregistrement terminé !</p>
                <div className="space-y-1.5">
                  <button
                    onClick={onDownloadVideo}
                    className="w-full py-2 rounded-lg text-[10px] font-bold bg-gray-900 text-white hover:bg-gray-800 transition-colors flex items-center justify-center gap-1.5"
                    data-testid="download-rec-btn"
                  >
                    <Download size={10} /> Télécharger WebM
                  </button>
                  {!mp4Ready ? (
                    <button
                      onClick={onConvertMp4}
                      disabled={mp4Converting}
                      className="w-full py-2 rounded-lg text-[10px] font-bold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
                      data-testid="convert-mp4-btn"
                    >
                      {mp4Converting ? <><Loader2 size={10} className="animate-spin" /> Conversion en MP4...</> : <><FileVideo size={10} /> Convertir en MP4</>}
                    </button>
                  ) : (
                    <a
                      href={mp4Url}
                      download
                      className="w-full py-2 rounded-lg text-[10px] font-bold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors flex items-center justify-center gap-1.5"
                      data-testid="download-mp4-btn"
                    >
                      <Download size={10} /> Télécharger MP4
                    </a>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* STOP button — always visible when recording (no panel needed) */}
        {isRecordingVideo && (
          <button
            onClick={stopVideoRecording}
            className="bg-gray-900 text-white px-4 py-2.5 rounded-xl shadow-2xl flex items-center gap-2 text-xs font-bold hover:bg-gray-800 transition-colors animate-in fade-in"
            data-testid="stop-rec-btn"
          >
            <div className="w-3 h-3 rounded-sm bg-rose-500" />
            Arreter l'enregistrement
          </button>
        )}

        {/* Bottom controls row */}
        <div className="flex items-center gap-1.5">
          {/* REC toggle — opens panel when not recording */}
          {!isRecordingVideo && (
            <button
              onClick={() => setShowRecPanel(v => !v)}
              className={`h-9 px-3 rounded-lg shadow-lg flex items-center justify-center gap-1.5 transition-colors text-[10px] font-bold ${
                videoReady
                  ? "bg-emerald-600 text-white"
                  : "bg-white/90 backdrop-blur-sm border border-gray-200 text-gray-600 hover:text-rose-600"
              }`}
              data-testid="video-rec-btn"
              title="Enregistrement video"
            >
              {videoReady ? <Download size={10} /> : <Circle size={10} className="fill-rose-500" />}
              {videoReady ? "VIDEO" : "REC"}
              {!videoReady && !isEnt && <Crown size={8} className="text-amber-400 ml-0.5" />}
            </button>
          )}
          <div className="w-px h-6 bg-gray-200" />
          {/* Voice select */}
          <select
            value={ttsVoice}
            onChange={(e) => setTtsVoice(e.target.value)}
            className="bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg shadow-lg text-[10px] font-medium text-gray-600 px-2 py-1.5 focus:outline-none"
            data-testid="tts-voice-select"
          >
            <option value="nova">Claire (féminine)</option>
            <option value="shimmer">Sophie (douce)</option>
            <option value="onyx">Pierre (masculine)</option>
            <option value="echo">Marc (posée)</option>
          </select>
          {/* TTS toggle */}
          <button
            onClick={() => {
              if (ttsEnabled) { setTtsEnabled(false); return; }
              if (!requireEnterprise("Voix off TTS")) return;
              setTtsEnabled(true);
            }}
            className={`w-9 h-9 rounded-lg shadow-lg flex items-center justify-center transition-colors ${
              ttsEnabled
                ? "bg-violet-600 text-white"
                : "bg-white/90 backdrop-blur-sm border border-gray-200 text-gray-400 hover:text-gray-600"
            }`}
            data-testid="tts-toggle"
            title={ttsEnabled ? "Désactiver la voix" : "Activer la voix"}
          >
            {ttsLoading ? <Loader2 size={14} className="animate-spin" /> : ttsEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
          </button>
        </div>
      </div>

      {/* Keyboard hint */}
      <div className="absolute bottom-6 left-6 z-50 hidden md:block">
        <span className="text-[10px] text-gray-400 bg-white/70 backdrop-blur-sm border border-gray-200 px-3 py-1.5 rounded-full">
          {autoPlaying ? "Lecture auto en cours" : "Fleches / Espace : naviguer"} &middot; Echap : quitter
        </span>
      </div>
    </>
  );
}
